import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, PackageMinus, Search, Trash2 } from 'lucide-react';
import { PURCHASE_RETURN_REASON } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn, dateOnly, humanize, money, newIdempotencyKey } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Badge, Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { PrintDocumentButton } from './PrintDocumentButton';
import { priceVarianceNote } from './payables-view';
import {
  BATCH_SEARCH_LIMIT,
  Detail,
  batchLabel,
  storeLabel,
  supplierLabel,
  useSuppliers,
  type PurchaseReturnResult,
  type ReturnLineDraft,
  type StockBatch,
  type StoreOption,
} from './PayablesPage';

const RETURNS_TAB = '/payables?tab=returns';

export function ReturnGoodsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), RETURNS_TAB);
  const suppliers = useSuppliers();
  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
  });
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [supplierId, setSupplierId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [reason, setReason] = useState('');
  const [creditNoteNo, setCreditNoteNo] = useState('');
  const [notes, setNotes] = useState('');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState<{ storeId: string; q: string } | null>(null);
  const [lines, setLines] = useState<ReturnLineDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<PurchaseReturnResult | null>(null);

  const found = useQuery({
    queryKey: ['return-batch-search', search?.storeId, search?.q],
    queryFn: () => get<{ data: StockBatch[] }>(`/pharmacy/stock${qs({ storeId: search?.storeId, q: search?.q, limit: BATCH_SEARCH_LIMIT })}`),
    enabled: search !== null,
  });

  const candidates = (found.data?.data ?? []).filter((batch) => !lines.some((line) => line.batch.id === batch.id));

  const lineValue = (line: ReturnLineDraft): number => {
    const qty = Number(line.qty);
    return Number.isFinite(qty) && qty > 0 ? Math.round(qty * line.batch.purchasePriceMinor) : 0;
  };
  const lineInvalid = (line: ReturnLineDraft): boolean => {
    const qty = Number(line.qty);
    return !(qty > 0) || qty > line.batch.qtyOnHand;
  };
  const totalValueMinor = lines.reduce((sum, line) => sum + lineValue(line), 0);

  const problems: string[] = [];
  if (!supplierId) problems.push('a supplier');
  if (!storeId) problems.push('a store');
  if (!reason) problems.push('a reason');
  if (lines.length === 0) problems.push('at least one batch');
  if (lines.some(lineInvalid)) problems.push('quantities within what is on hand');

  const giveBack = useMutation({
    mutationFn: () =>
      post<PurchaseReturnResult>(
        '/pharmacy/purchase-returns',
        {
          supplierId,
          storeId,
          reason,
          creditNoteNo: creditNoteNo.trim() || undefined,
          notes: notes.trim() || undefined,
          lines: lines.map((line) => ({ stockBatchId: line.batch.id, qty: Number(line.qty) })),
        },
        { idempotencyKey },
      ),
    onSuccess: (result) => {
      const supplierName = suppliers.data?.find((supplier) => supplier.id === supplierId)?.name ?? 'the supplier';
      toast.success(tr('pharmacy', 'debit_note_x', { number: result.number }), tr('pharmacy', 'x_credited_to_x', { totalMinor: money(result.totalMinor), supplierName: supplierName }));
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      setCreated(result);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const runSearch = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (!storeId) return;
    const next = { storeId, q: searchText.trim() };
    if (search && search.storeId === next.storeId && search.q === next.q) {
      void found.refetch();
      return;
    }
    setSearch(next);
  };

  const addBatch = (batch: StockBatch) => setLines((current) => [...current, { batch, qty: String(Math.min(1, batch.qtyOnHand)) }]);

  const dirty = !giveBack.isSuccess && (supplierId !== '' || storeId !== '' || reason !== '' || creditNoteNo !== '' || notes !== '' || lines.length > 0);

  if (created) {
    const varianceNote = priceVarianceNote(created, money);
    return (
      <FormScreen
        title={tr('pharmacy', 'debit_note_x', { number: created.number })}
        description={tr('pharmacy', 'the_goods_have_left_the_store_and_the_supplier')}
        eyebrow={tr('pharmacy', 'supplier_payables')}
        icon={PackageMinus}
        backTo={returnTo}
      >
        <FormSection columns={1}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Detail label={tr('pharmacy', 'credited_to_the_supplier')}>
              <span className="tabular">{money(created.totalMinor)}</span>
            </Detail>
            {created.stockCostMinor !== undefined && (
              <Detail label={tr('pharmacy', 'stock_left_at_cost')}>
                <span className="tabular">{money(created.stockCostMinor)}</span>
              </Detail>
            )}
          </dl>
          {varianceNote && <Alert>{varianceNote}</Alert>}
          <div className="flex flex-wrap justify-end gap-2">
            <PrintDocumentButton path={`/pharmacy/print/debit-note/${created.id}`} label={tr('pharmacy', 'print_debit_note')} size="default" />
            <Button type="button" onClick={() => navigate(returnTo, { replace: true })}>
              {tr('pharmacy', 'done')}
            </Button>
          </div>
        </FormSection>
      </FormScreen>
    );
  }

  return (
    <FormScreen
      title={tr('pharmacy', 'return_goods_to_a_supplier')}
      description={tr('pharmacy', 'the_stock_leaves_at_cost_and_the_supplier_is')}
      eyebrow={tr('pharmacy', 'supplier_payables')}
      icon={PackageMinus}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (problems.length > 0) return;
        setError(null);
        giveBack.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={PackageMinus}>
        <Field
          label={tr('pharmacy', 'supplier')}
          required
          hint={suppliers.data && suppliers.data.length === 0 ? tr('pharmacy', 'none_yet_add_one_on_the_suppliers_tab_of') : tr('pharmacy', 'only_batches_received_from_this_supplier_can_be')}
        >
          <Select value={supplierId} onChange={(event) => setSupplierId(event.target.value)}>
            <option value="">{tr('pharmacy', 'select')}</option>
            {suppliers.data?.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplierLabel(supplier)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'store')} required hint={lines.length > 0 ? tr('pharmacy', 'remove_the_batches_below_to_change_the_store') : undefined}>
          <Select
            value={storeId}
            disabled={lines.length > 0}
            onChange={(event) => {
              setStoreId(event.target.value);
              setSearch(null);
            }}
          >
            <option value="">{tr('pharmacy', 'select')}</option>
            {stores.data?.map((store) => (
              <option key={store.id} value={store.id}>
                {storeLabel(store)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'reason')} required>
          <Select value={reason} onChange={(event) => setReason(event.target.value)}>
            <option value="">{tr('pharmacy', 'select')}</option>
            {PURCHASE_RETURN_REASON.map((known) => (
              <option key={known} value={known}>
                {humanize(known)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'supplier_credit_note_no')} hint={tr('pharmacy', 'optional_the_number_on_the_suppliers_own_credit')}>
          <Input value={creditNoteNo} maxLength={80} onChange={(event) => setCreditNoteNo(event.target.value)} />
        </Field>
        <Field label={tr('pharmacy', 'notes')} className="sm:col-span-2">
          <Textarea rows={2} maxLength={600} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'batches_to_return')} icon={ListChecks} columns={1}>
        <div className="flex gap-2">
          <Input
            placeholder={storeId ? tr('pharmacy', 'search_stock_by_name_code_or_barcode_then_press') : tr('pharmacy', 'choose_a_store_first')}
            aria-label={tr('pharmacy', 'search_stock')}
            value={searchText}
            disabled={!storeId}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') runSearch(event);
            }}
          />
          <Button type="button" variant="secondary" disabled={!storeId} loading={found.isFetching} onClick={runSearch}>
            <Search className="h-4 w-4" /> {tr('pharmacy', 'search')}
          </Button>
        </div>

        {found.error && <Alert variant="destructive">{describeError(found.error)}</Alert>}
        {search && found.data && (
          <div className="max-h-48 overflow-y-auto rounded-md border">
            {candidates.length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">{tr('pharmacy', 'no_batches_in_stock_match_that_search_or_they')}</p>
            ) : (
              <ul className="divide-y">
                {candidates.map((batch) => (
                  <li key={batch.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-accent/50 focus-ring"
                      onClick={() => addBatch(batch)}
                    >
                      <span>
                        <span className="font-medium">{batchLabel(batch)}</span>
                        <span className="ms-2 text-xs text-muted-foreground">
                          {batch.product.code} {tr('pharmacy', 'batch_2')} {batch.batchNo} {tr('pharmacy', 'expires_2')} {dateOnly(batch.expiryDate)}
                        </span>
                        {batch.status && batch.status !== 'active' && (
                          <Badge variant={batch.status === 'recalled' ? 'destructive' : 'warning'} className="ms-2">
                            {humanize(batch.status)}
                          </Badge>
                        )}
                      </span>
                      <span className="whitespace-nowrap text-xs tabular text-muted-foreground">
                        {batch.qtyOnHand} {batch.product.unit} · {money(batch.purchasePriceMinor)} {tr('pharmacy', 'each')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {found.data && found.data.data.length >= BATCH_SEARCH_LIMIT && (
          <p className="text-xs text-muted-foreground">
            {tr('pharmacy', 'showing_the_first')} {BATCH_SEARCH_LIMIT} {tr('pharmacy', 'batches_narrow_the_search_to_find_others')}
          </p>
        )}

        {lines.length === 0 ? (
          <div className="rounded-lg border p-6 text-center text-sm text-muted-foreground">{tr('pharmacy', 'search_for_a_batch_and_click_it_to_add_it_to')}</div>
        ) : (
          <div className="space-y-2">
            {lines.map((line, index) => {
              const invalid = lineInvalid(line);
              return (
                <div key={line.batch.id} className="grid items-center gap-2 rounded-md border p-2 sm:grid-cols-[1fr_120px_110px_32px]">
                  <div className="text-sm">
                    <p className="font-medium">{batchLabel(line.batch)}</p>
                    <p className="text-xs text-muted-foreground">
                      {tr('pharmacy', 'batch_3')} {line.batch.batchNo} {tr('pharmacy', 'on_hand_2')} {line.batch.qtyOnHand} {line.batch.product.unit} ·{' '}
                      {money(line.batch.purchasePriceMinor)} {tr('pharmacy', 'each')}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={line.batch.qtyOnHand}
                    step="any"
                    aria-label={tr('pharmacy', 'quantity_of_x_batch_x_to_return', { batch: batchLabel(line.batch), batchNo: line.batch.batchNo })}
                    aria-invalid={invalid}
                    className={cn('tabular', invalid && 'border-destructive')}
                    value={line.qty}
                    onChange={(event) =>
                      setLines((current) => current.map((existing, position) => (position === index ? { ...existing, qty: event.target.value } : existing)))
                    }
                  />
                  <p className="text-end text-sm tabular">{money(lineValue(line))}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={tr('pharmacy', 'remove')}
                    onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{tr('pharmacy', 'value_at_cost_before_tax_the_debit_note_adds')}</span>
              <strong className="tabular">{money(totalValueMinor)}</strong>
            </div>
          </div>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={tr('pharmacy', 'return_goods')}
        loading={giveBack.isPending}
        incomplete={problems.length > 0}
        cancelTo={returnTo}
        note={problems.length > 0 ? `${tr('pharmacy', 'still_needed')} ${problems.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
